import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, CardActionArea, Typography, Chip, Avatar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Factory as ProductionIcon,
  VerifiedUser as QualityIcon,
  Build as MaintenanceIcon,
  EventNote as PlanningIcon,
  BarChart as AnalyticsIcon,
  Devices as ShopFloorIcon,
  Storage as MasterDataIcon,
  Restaurant as KitchenOpsIcon,
  SyncAlt as ERPIcon,
  LocalShipping as SupplyChainIcon,
  AdminPanelSettings as AdminIcon,
  Grain as MillingIcon,
  QrCode2 as QRIcon,
} from '@mui/icons-material';

interface Module {
  title: string;
  description: string;
  path: string;
  icon: React.ReactElement;
  color: string;
  tags: string[];
}

const modules: Module[] = [
  {
    title: 'Dashboard',
    description: 'Real-time OEE metrics, equipment status, production trends, and live alerts across all operations.',
    path: '/dashboard',
    icon: <DashboardIcon sx={{ fontSize: 36 }} />,
    color: '#1976d2',
    tags: ['OEE', 'KPIs', 'Alerts'],
  },
  {
    title: 'Production',
    description: 'Manage work orders, track production runs, monitor output quantities, and view shift performance.',
    path: '/production',
    icon: <ProductionIcon sx={{ fontSize: 36 }} />,
    color: '#388e3c',
    tags: ['Work Orders', 'Shifts', 'Output'],
  },
  {
    title: 'Supply Chain',
    description: 'Purchase orders, sales orders, ATP (Available-to-Promise) checking, and shipping advisory for accurate delivery commitments.',
    path: '/supply-chain',
    icon: <SupplyChainIcon sx={{ fontSize: 36 }} />,
    color: '#0288d1',
    tags: ['POs', 'SOs', 'ATP', 'Shipping'],
  },
  {
    title: 'Planning',
    description: 'Material Requirements Planning (MRP), Master Production Schedule (MPS), capacity requirements, and job scheduling.',
    path: '/planning',
    icon: <PlanningIcon sx={{ fontSize: 36 }} />,
    color: '#7b1fa2',
    tags: ['MRP', 'MPS', 'Capacity', 'Scheduling'],
  },
  {
    title: 'Quality',
    description: 'Inspection management, quality control plans, non-conformance tracking, and certificate of analysis generation.',
    path: '/quality',
    icon: <QualityIcon sx={{ fontSize: 36 }} />,
    color: '#f57c00',
    tags: ['Inspections', 'NCR', 'CoA'],
  },
  {
    title: 'Maintenance',
    description: 'Preventive and corrective maintenance scheduling, equipment downtime tracking, and work order management for assets.',
    path: '/maintenance',
    icon: <MaintenanceIcon sx={{ fontSize: 36 }} />,
    color: '#c62828',
    tags: ['PM', 'CM', 'Downtime', 'Assets'],
  },
  {
    title: 'Analytics',
    description: 'Advanced reporting, trend analysis, predictive insights, and custom KPI dashboards across all modules.',
    path: '/analytics',
    icon: <AnalyticsIcon sx={{ fontSize: 36 }} />,
    color: '#00695c',
    tags: ['Reports', 'Trends', 'Predictive'],
  },
  {
    title: 'Flour Milling',
    description: 'End-to-end flour milling operations: grain intake, silo management, milling orders, grist recipes, quality tests, and fortification.',
    path: '/milling',
    icon: <MillingIcon sx={{ fontSize: 36 }} />,
    color: '#8d6e63',
    tags: ['Grain', 'Silos', 'Recipes', 'Fortification'],
  },
  {
    title: 'Kitchen Ops',
    description: 'Recipe management, batch production, ingredient tracking, and kitchen workflow management for food production.',
    path: '/kitchen-ops',
    icon: <KitchenOpsIcon sx={{ fontSize: 36 }} />,
    color: '#e65100',
    tags: ['Recipes', 'Batches', 'Ingredients'],
  },
  {
    title: 'Shop Floor',
    description: 'Real-time shop floor visibility, machine status monitoring, operator assignments, and production cell management.',
    path: '/shopfloor',
    icon: <ShopFloorIcon sx={{ fontSize: 36 }} />,
    color: '#455a64',
    tags: ['Machines', 'Operators', 'Real-time'],
  },
  {
    title: 'Master Data',
    description: 'Centralized management of items, BOMs, routings, work centres, vendors, customers, and reference data.',
    path: '/master-data',
    icon: <MasterDataIcon sx={{ fontSize: 36 }} />,
    color: '#37474f',
    tags: ['Items', 'BOM', 'Routings', 'Vendors'],
  },
  {
    title: 'ERP Integration',
    description: 'NetSuite ERP synchronization status, integration health monitoring, and data mapping configuration.',
    path: '/erp',
    icon: <ERPIcon sx={{ fontSize: 36 }} />,
    color: '#1565c0',
    tags: ['NetSuite', 'Sync', 'Integration'],
  },
  {
    title: 'QR Manager',
    description: 'Generate and manage QR codes for work orders, materials, equipment, and locations for shop floor scanning.',
    path: '/qr-manager',
    icon: <QRIcon sx={{ fontSize: 36 }} />,
    color: '#4a148c',
    tags: ['QR Codes', 'Labels', 'Scanning'],
  },
  {
    title: 'Admin',
    description: 'User management, role-based access control, system configuration, audit logs, and application settings.',
    path: '/admin',
    icon: <AdminIcon sx={{ fontSize: 36 }} />,
    color: '#b71c1c',
    tags: ['Users', 'Roles', 'Settings', 'Audit'],
  },
];

const ModulesDashboard: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          MES Modules
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a module to navigate to it. Each module provides specialised tools for managing your manufacturing operations.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {modules.map((mod) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={mod.path}>
            <Card
              sx={{
                height: '100%',
                borderTop: `4px solid ${mod.color}`,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 6 },
              }}
            >
              <CardActionArea
                onClick={() => navigate(mod.path)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0 }}
              >
                <CardContent sx={{ width: '100%', flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={2} gap={1.5}>
                    <Avatar sx={{ bgcolor: mod.color, width: 52, height: 52 }}>
                      {mod.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
                      {mod.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 56 }}>
                    {mod.description}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {mod.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ModulesDashboard;
